// Phase 1A Step 34 — Calculator hero number + dual paths sub-line.
// Extracted from Calculator.tsx for LOC ceiling. Renders the big animated
// hero target plus the "if you stop early / ceiling" comparison row.

import type { ChainRow } from "./calc-utils.ts";
import { fmtBig, useCountUp } from "./calc-utils.ts";

interface CalculatorHeroProps {
  mode: "protected" | "full";
  weeks: number;
  stake: number;
  odds: number;
  takeout: number;
  symbol: string;
  heroTarget: number;
  fullCeiling: number;
  chain: ChainRow[];
}

export function CalculatorHero({
  mode,
  weeks,
  stake,
  odds,
  takeout,
  symbol,
  heroTarget,
  fullCeiling,
  chain,
}: CalculatorHeroProps): JSX.Element {
  const animatedHero = useCountUp(heroTarget, 900);
  const tkPct = Math.round(takeout * 100);
  const earlyStopValue = weeks > 1 ? chain[weeks - 2]!.pocketIfStop : chain[0]!.pocketIfStop;

  return (
    <div
      className="sz-l-calc-hero-block"
      style={{
        position: "relative",
        marginTop: 4,
        marginBottom: 22,
        marginLeft: -32,
        marginRight: -80,
        padding: "20px 32px",
        borderTop: "1px solid var(--border)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div
        className="eyebrow-muted"
        style={{ marginBottom: 8, display: "flex", justifyContent: "space-between" }}
      >
        <span>
          {mode === "protected"
            ? `Total in your pocket · ${weeks} weeks`
            : `Week ${weeks} target · all in`}
        </span>
        <span style={{ color: "var(--accent-dim)" }}>
          {mode === "protected" ? "Real money" : "Hypothetical"}
        </span>
      </div>
      <div
        className="display num"
        style={{
          color: "var(--accent)",
          fontSize: "clamp(60px, 8.5vw, 124px)",
          fontWeight: 400,
          lineHeight: 0.86,
          textShadow: "0 0 30px rgba(212, 168, 67, 0.18)",
          whiteSpace: "nowrap",
          overflow: "visible",
        }}
      >
        <span
          style={{
            fontSize: "0.55em",
            letterSpacing: "0.02em",
            color: "var(--accent)",
            verticalAlign: "0.18em",
            marginRight: "0.08em",
          }}
        >
          {symbol}
        </span>
        {fmtBig(animatedHero)}
      </div>
      <div
        className="mono"
        style={{
          marginTop: 10,
          fontSize: 11,
          color: "var(--text-muted)",
          letterSpacing: "0.08em",
        }}
      >
        {mode === "protected" ? (
          <>
            FROM {symbol}
            {stake.toLocaleString()} · {tkPct}% TAKEN OUT WEEKLY · {weeks} WINS
          </>
        ) : (
          <>
            FROM {symbol}
            {stake.toLocaleString()} · {odds}× COMPOUND · {weeks} CONSECUTIVE WINS
          </>
        )}
      </div>

      <div
        className="sz-l-calc-paths"
        style={{
          marginTop: 14,
          paddingTop: 14,
          borderTop: "1px dashed var(--border-bright)",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
        }}
      >
        <div>
          <div
            className="mono"
            style={{
              fontSize: 9,
              letterSpacing: "0.16em",
              color: "var(--text-dim)",
              textTransform: "uppercase",
            }}
          >
            If you stop early (Wk {Math.max(1, weeks - 1)})
          </div>
          <div className="mono" style={{ fontSize: 14, color: "var(--text)", marginTop: 4 }}>
            {symbol}
            {fmtBig(earlyStopValue)}
          </div>
        </div>
        <div>
          <div
            className="mono"
            style={{
              fontSize: 9,
              letterSpacing: "0.16em",
              color: "var(--accent-dim)",
              textTransform: "uppercase",
            }}
          >
            Ceiling · Full rollover
          </div>
          <div className="mono" style={{ fontSize: 14, color: "var(--accent)", marginTop: 4 }}>
            {symbol}
            {fmtBig(fullCeiling)}
          </div>
        </div>
      </div>
    </div>
  );
}
